import { Flex } from '@chakra-ui/react'
import { COLORS, ColorTokens } from '@ds/tokens/legacy/colors'
import { Dispatch, SetStateAction, useRef } from 'react'

import { useAppSelector } from '@/app/hooks'
import {
	COLLAPSED_HOME_SIDEBAR_WIDTH_PX,
	EXPANDED_HOME_SIDEBAR_WIDTH_PX,
	HOME_SIDEBAR_TRANSITION,
} from '@/common/constants/sidebar'
import { selectIsNotificationsOpen } from '@/modules/notifications/slice/selectors'

type Props = {
	children: React.ReactNode
	isHovered: boolean
	setIsHovered: Dispatch<SetStateAction<boolean>>
}

export const SidebarLayout = ({ children, isHovered, setIsHovered }: Props) => {
	const collapseTimeoutRef = useRef<NodeJS.Timeout | null>(null)
	const isNotificationsOpen = useAppSelector(selectIsNotificationsOpen)

	const sidebarWidth =
		isHovered || isNotificationsOpen
			? EXPANDED_HOME_SIDEBAR_WIDTH_PX
			: COLLAPSED_HOME_SIDEBAR_WIDTH_PX

	const handleMouseLeave = () => {
		if (collapseTimeoutRef.current) {
			clearTimeout(collapseTimeoutRef.current)
		}

		// Don't collapse if notifications are open
		if (!isNotificationsOpen) {
			collapseTimeoutRef.current = setTimeout(() => {
				setIsHovered(false)
			}, 100)
		}
	}

	const handleMouseEnter = () => {
		if (collapseTimeoutRef.current) {
			clearTimeout(collapseTimeoutRef.current)
			collapseTimeoutRef.current = null
		}
	}

	return (
		<Flex
			flexDir="column"
			bg={ColorTokens.white}
			h="100vh"
			w={sidebarWidth}
			flexShrink={0}
			justifyContent="space-between"
			borderRightWidth="1px"
			borderColor={COLORS.gray[4]}
			transition={HOME_SIDEBAR_TRANSITION}
			position="absolute"
			left="0"
			top="0"
			zIndex="10"
			onMouseLeave={handleMouseLeave}
			onMouseEnter={handleMouseEnter}
		>
			{children}
		</Flex>
	)
}
